import { useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { changePassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const MIN = 8;

export default function ChangePasswordPage() {
  const [, setLocation] = useLocation();
  const { user, mustChangePassword, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setOk(false);
    if (next.length < MIN) {
      setErr(`Новый пароль должен быть не короче ${MIN} символов`);
      return;
    }
    if (next !== confirm) {
      setErr("Пароли не совпадают");
      return;
    }
    if (next === current) {
      setErr("Новый пароль должен отличаться от текущего");
      return;
    }
    setPending(true);
    try {
      await changePassword(current, next);
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      await refresh();
      if (mustChangePassword) {
        // После смены по принудительной причине отправим на главную —
        // сервер уже снял флаг, Gate пустит пользователя в кабинет.
        setTimeout(() => setLocation("/"), 500);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось сменить пароль");
    } finally {
      setPending(false);
    }
  }

  const forced = mustChangePassword;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--background, #0f1419)" }}
    >
      <Card
        className="w-full max-w-md border-[var(--sidebar-border)]"
        style={{ background: "var(--card)" }}
      >
        <CardHeader>
          <CardTitle>Смена пароля</CardTitle>
          <CardDescription>
            {forced
              ? "Администратор сбросил ваш пароль. Придумайте новый пароль, чтобы продолжить работу."
              : `Пользователь: ${user?.login ?? ""}. Минимальная длина пароля — ${MIN} символов.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            {err ? (
              <div className="text-sm text-red-600 dark:text-red-400" role="alert">
                {err}
              </div>
            ) : null}
            {ok ? (
              <div className="text-sm text-green-700" role="status">
                Пароль сохранён.
              </div>
            ) : null}
            <div className="space-y-2">
              <Label htmlFor="current">
                {forced ? "Временный пароль от администратора" : "Текущий пароль"}
              </Label>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">Новый пароль</Label>
              <Input
                id="new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                minLength={MIN}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Повторите новый пароль</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                minLength={MIN}
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={pending} className="flex-1">
                {pending ? "Сохраняю…" : "Сохранить пароль"}
              </Button>
              {forced ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    void logout().then(() => setLocation("/login"));
                  }}
                  disabled={pending}
                >
                  Выйти
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/")}
                  disabled={pending}
                >
                  Отмена
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
